import PropTypes from 'prop-types';
import { forwardRef, useMemo } from 'react';
import ACLTableServerSideTwoUrl from './ACLTableServerSideTwoUrl';
import ACLTableClientSide from './ACLTableClientSide';
import { getIdsInValue } from './utils';
import ACLTableServerSideOneUrl from './ACLTableServerSideOneUrl';

const ACLTable = forwardRef(function ACLTable(props, ref) {
  const {
    position,
    value,
    columnDefs,
    multiple,
    selectedKeys: selectedKeysProp,
    stringEqual,
    defaultColDef: defaultColDefProp,
    listUrl,
    listIdsUrl,
    listByIdsUrl,
    rowModelType,
    ...other
  } = props;

  // If not multiple, need to compute the default selectedKeys
  const selectedKeys = selectedKeysProp || getIdsInValue(value, stringEqual);

  const defaultColDef = useMemo(() => {
    return {
      ...defaultColDefProp,
      flex: multiple ? undefined : 1,
      minWidth: 100,
      width: 100,
      resizable: true,
    };
  }, [defaultColDefProp, multiple]);

  const common = {
    ...other,
    position,
    columnDefs,
    multiple,
    defaultColDef,
    selectedKeys,
    stringEqual,
    className: 'acl-table',
    headerHeight: 40,
    rowHeight: 40,
    animateRows: false,
    suppressAddButton: true,
    suppressDeleteButton: true,
    fuzzySearchOpen: true,
    suppressColumnSelect: true,
    suppressExcelExport: true,
    suppressCsvExport: true,
    suppressToolbarRefresh: true,
    suppressTableSetting: true,
    suppressFullscreen: true,
    suppressStatusBarTotalRows: multiple,
  };

  // 如果特别指定了不需要懒加载，则一次加载完所有数据，不适合数据量大的情况。
  if (rowModelType === 'clientSide') {
    return <ACLTableClientSide {...common} ref={ref} />;
  }

  // 使用先 fetch 所有 ids 再拿详细信息的 lazy load 模式，此时可以支持多选所有 ids。
  if ((position === 'right' && listByIdsUrl) || (!listUrl && listIdsUrl && listByIdsUrl)) {
    return <ACLTableServerSideTwoUrl {...common} ref={ref} />;
  }

  if (rowModelType === 'serverSide') {
    return <ACLTableServerSideOneUrl {...common} listIdsUrl={listIdsUrl} ref={ref} />;
  }

  // 一次加载完所有数据，不适合数据量大的情况。
  return <ACLTableClientSide {...common} ref={ref} />;
});

ACLTable.propTypes = {
  position: PropTypes.oneOf(['left', 'right']),
  value: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
      label: PropTypes.string,
    }),
  ),
  columnDefs: PropTypes.arrayOf(PropTypes.shape({})),
  multiple: PropTypes.bool,
  stringEqual: PropTypes.bool,
  mapping: PropTypes.shape({
    value: PropTypes.string,
    label: PropTypes.string,
  }),
  selectedKeys: PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.number, PropTypes.string])),
  onSelectionChanged: PropTypes.func,
  defaultColDef: PropTypes.shape({}),
  listUrl: PropTypes.string,
  listIdsUrl: PropTypes.string,
  listByIdsUrl: PropTypes.string,
  rowModelType: PropTypes.string,
};

export default ACLTable;
